import React, { useMemo } from "react";

/**
 * Tiny SVG sparkline (12 weekly buckets by default).
 * Renders a smooth polyline + faint area fill below.
 * Props:
 *   data: number[]   — required, array of values (order counts per week)
 *   width: number    — default 80
 *   height: number   — default 22
 *   color: string    — stroke color (default Klein blue)
 */
export default function Sparkline({
  data = [],
  width = 80,
  height = 22,
  color = "#2563eb",
}) {
  const { points, areaPath, hasData, max } = useMemo(() => {
    if (!data || data.length === 0) {
      return { points: "", areaPath: "", hasData: false, max: 0 };
    }
    const maxVal = Math.max(...data, 1);
    const stepX = data.length > 1 ? width / (data.length - 1) : 0;
    const pad = 1.5; // top/bottom padding
    const usableH = height - pad * 2;
    const pts = data.map((v, i) => {
      const x = i * stepX;
      const y = height - pad - (v / maxVal) * usableH;
      return [x, y];
    });
    const polyline = pts.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
    const area =
      `M ${pts[0][0].toFixed(2)},${height} ` +
      pts.map(([x, y]) => `L ${x.toFixed(2)},${y.toFixed(2)}`).join(" ") +
      ` L ${pts[pts.length - 1][0].toFixed(2)},${height} Z`;
    return {
      points: polyline,
      areaPath: area,
      hasData: data.some((v) => v > 0),
      max: maxVal,
    };
  }, [data, width, height]);

  if (!data || data.length === 0) {
    return (
      <span className="inline-block text-slate-300 text-xs italic">—</span>
    );
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
      aria-label={`Activity sparkline (max ${max})`}
    >
      {hasData ? (
        <>
          <path d={areaPath} fill={color} fillOpacity={0.12} />
          <polyline
            fill="none"
            stroke={color}
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
            points={points}
          />
          {/* End-of-line dot */}
          {data[data.length - 1] > 0 && (
            <circle
              cx={width}
              cy={
                height -
                1.5 -
                ((data[data.length - 1] / max) * (height - 3))
              }
              r={2}
              fill={color}
            />
          )}
        </>
      ) : (
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke="#e2e8f0"
          strokeWidth={1.5}
          strokeDasharray="2 2"
        />
      )}
    </svg>
  );
}
